import '../css/timeline.css';
import { appState } from './stateManager';
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';

import { STEP_INTERVAL, TOOLTIP_ARROW } from './timeline.details';

export default class Timeline {
	constructor() {
		this.reverseBtn = document.getElementById('timeline-reverse');
		this.pauseBtn = document.getElementById('timeline-pause');
		this.playBtn = document.getElementById('timeline-play');
		this.hasSetup = false;
		this.minTimestamp = null;
		this.maxTimestamp = null;
		this.labelMin = document.getElementById('timeline-label-min');
		this.labelMax = document.getElementById('timeline-label-max');
		this.legendValuePresent = document.getElementById('timeline-legend-value-present');
		this.legendValuePast = document.getElementById('timeline-legend-value-past');
		this.legendValueFuture = document.getElementById('timeline-legend-value-future');
		this.interval = null;
		this.sliderValues = [];
		this.sliderIndex = null;
	}

	init() {
		appState.subscribe(this.onStateChange.bind(this));
		this.setupControls();
	}

	setupControls() {
		if (!this.reverseBtn || !this.pauseBtn || !this.playBtn) return;
		this.pauseBtn.onclick = () => {
			this.stopPlayInterval();
		};
		this.playBtn.onclick = () => {
			this.startPlayInterval();
		};
		this.reverseBtn.onclick = () => {
			this.startPlayInterval(true);
		};
	}

	startPlayInterval(isReverse = false) {
		this.stopPlayInterval();
		if (this.interval) return;
		isReverse ? this.reverseBtn?.classList.add('active') : this.playBtn?.classList.add('active');
		const currentValue = this.slider.get(true);
		this.sliderIndex = this.sliderValues.indexOf(currentValue);
		if (this.sliderIndex < 0) return;
		this.interval = setInterval(() => {
			this.onPlayInterval(isReverse);
		}, STEP_INTERVAL);
		this.onPlayInterval(isReverse);
	}

	onPlayInterval(isReverse = false) {
		let nextIndex;

		if (isReverse) {
			nextIndex = this.sliderIndex - 1;
			if (nextIndex < 0) nextIndex = this.sliderValues.length - 1;
		} else {
			nextIndex = this.sliderIndex + 1;
			if (nextIndex > this.sliderValues.length - 1) nextIndex = 0;
		}

		const nextValue = this.sliderValues[nextIndex];

		this.slider?.updateOptions({ animate: false });
		// jump without animation → no flickering
		this.slider?.set(nextValue, false, false, { animate: false });

		this.sliderIndex = nextIndex;
	}

	stopPlayInterval() {
		if (!this.interval) return;
		clearInterval(this.interval);
		this.interval = null;
		this.sliderIndex = null;
		this.playBtn?.classList.remove('active');
		this.reverseBtn?.classList.remove('active');
	}

	setup(data) {
		if (this.slider) return;
		const wrapper = document.getElementById('timeline-slider');
		if (!wrapper) return;
		const range = this.getRange(data);

		//removed min, max values, so it can clean restart on end, and no jumps.
		let timestamps = [];
		for (const item of data) {
			const startTimestamp = item.start_time;
			const endTimestamp = item.end_time;
			if (!timestamps.includes(startTimestamp)) timestamps.push(startTimestamp);
			if (!timestamps.includes(endTimestamp)) timestamps.push(endTimestamp);
		}
		timestamps = timestamps.filter((timestamp) => !isNaN(timestamp) && timestamp != null).sort((a, b) => a - b);

		this.sliderValues = timestamps;
		this.minTimestamp = range.min;
		this.maxTimestamp = range.max;
		this.slider = noUiSlider.create(wrapper, {
			start: range.min,
			connect: true,
			range,
			snap: true,
			tooltips: [
				{
					to: (value) => this.getTooltip(value)
				}
			]
		});
		this.slider.on('slide', (values) => {
			const timestamp = parseInt(values[0]);
			// console.log(timestamp);
			this.onSlide(timestamp);
		});

		this.slider.on('set', (values) => {
			const timestamp = parseInt(values[0]);
			// console.log(timestamp);
			this.onSlide(timestamp);
		});

		this.setupPips(range);
		setTimeout(() => {
			this.slider.set(range['50.00%']);
		});
	}

	setupPips(range) {
		const numPips = Object.keys(range).length || 0;
		if (!numPips) return;
		const pipsWrapper = document.getElementById('timeline-pips');
		if (!pipsWrapper) return;
		const fragment = new DocumentFragment();
		for (let p = 0; p < numPips; p++) {
			const pip = document.createElement('span');
			pip.classList.add('timeline-pip');
			fragment.appendChild(pip);
		}
		pipsWrapper.appendChild(fragment);
	}

	getTooltip(value) {
		return `${TOOLTIP_ARROW}<div id="timeline-tooltip-content">${this.getDateString(value)}</div>${TOOLTIP_ARROW}`;
	}

	getDateString(value) {
		const year = new Date(value).getFullYear();
		const month = (new Date(value).getMonth() + 1).toString().padStart(2, '0');
		const day = new Date(value).getDate().toString().padStart(2, '0');
		return `${year}.${month}.${day}.`;
	}

	getRange(data) {
		let timestamps = [];
		for (const item of data) {
			const startTimestamp = item.start_time;
			const endTimestamp = item.end_time;
			if (!timestamps.includes(startTimestamp)) timestamps.push(startTimestamp);
			if (!timestamps.includes(endTimestamp)) timestamps.push(endTimestamp);
		}
		timestamps = timestamps.filter((timestamp) => !isNaN(timestamp) && timestamp != null).sort((a, b) => a - b);
		const step = 100 / (timestamps.length - 1);
		const range = {};
		timestamps.forEach((timestamp, i) => {
			const pct = (i * step).toFixed(2) + '%';
			range[pct] = timestamp;
		});
		range.min = timestamps[0];
		range.max = timestamps[timestamps.length - 1];
		return range;
	}

	onSlide(timestamp) {
		if (isNaN(timestamp)) return;
		const currentState = appState.getState();
		appState.setState({ ...currentState, time: timestamp });
	}

	setupLabels() {
		if (!this.labelMin || !this.labelMax) return;
		this.labelMin.innerHTML = this.getDateString(this.minTimestamp);
		this.labelMax.innerHTML = this.getDateString(this.maxTimestamp);
	}

	updateLegend(presentCount = 0, pastCount = 0, visible = 0) {
		if (!this.legendValuePast || !this.legendValuePresent || !this.legendValueFuture) return;
		this.legendValuePast.innerHTML = pastCount;
		this.legendValuePresent.innerHTML = presentCount;
		this.legendValueFuture.innerHTML = (visible - pastCount - presentCount).toString() || '0';
	}

	onStateChange(state) {
		if (state.data && !this.hasSetup) {
			this.setup(state.data);
			this.setupLabels();
			const currentState = appState.getState();
			if (currentState.time !== this.minTimestamp) {
				appState.setState({ ...currentState, time: this.minTimestamp });
			}
			this.hasSetup = true;
		}

		this.updateLegend(state.presentIds?.size || 0, state.pastIds?.size || 0, state.visibleIds?.size);
	}
}
